import { useEffect, useRef, useSyncExternalStore } from 'react'
import { AppShell } from './components/AppShell'
import { GraphPanel } from './components/GraphPanel'
import { StructurePanel } from './components/StructurePanel'
import { Sidebar } from './components/Sidebar'
import { useENParser } from './hooks/useEN'
import { useTheme } from './contexts/ThemeContext'
import { usePlayground } from './store'
import { onKeyCombo } from './utils/keyboard'

// Embed mode — when the playground is loaded with ?view=<mode>, render
// a reduced pane layout instead of the full 3-column AppShell. Used by
// the landing /showcase page.
//
//   single panes   editor / canvas / structure    — one pane, full-bleed
//   pairs          split-canvas / split-structure — two panes side-by-
//                  side in one frame so they share the zustand store:
//                  editing the source on the left updates the right
//                  pane live, no iframe postMessage plumbing needed.
//
// Unrecognised values fall through to AppShell.
type ViewMode =
    | 'canvas' | 'editor' | 'structure'
    | 'split-canvas' | 'split-structure'
    | null
function readViewMode(): ViewMode {
    if (typeof window === 'undefined') return null
    const v = new URLSearchParams(window.location.search).get('view')
    if (v === 'canvas' || v === 'editor' || v === 'structure') return v
    if (v === 'split-canvas' || v === 'split-structure') return v
    return null
}

// Names produced by the seed-derived path (Material 3 / fallback themes).
// Anything OTHER than these is a curated theme — pass its display name to the parser.
const SEED_DERIVED_NAMES = new Set(['Indigo', 'Paper', 'Dark', 'Light', 'Random', 'Custom'])

// ── useIsMobile hook ──
const mobileQuery = typeof window !== 'undefined' ? window.matchMedia('(max-width: 1023px)') : null
function useIsMobile() {
    return useSyncExternalStore(
        cb => { mobileQuery?.addEventListener('change', cb); return () => mobileQuery?.removeEventListener('change', cb) },
        () => mobileQuery?.matches ?? false,
        () => false,
    )
}

function PlaygroundContent() {
    const source = usePlayground((s) => s.source)
    const groupBy = usePlayground((s) => s.groupBy)
    const direction = usePlayground((s) => s.direction)
    const isMobile = useIsMobile()

    // Sync isMobile to store
    useEffect(() => { usePlayground.setState({ isMobile }) }, [isMobile])

    // ── Parse ──
    const { themeName, theme } = useTheme()
    // Curated themes carry their REAL display name on theme.name (e.g. "Swiss", "Blueprint").
    // Seed-derived / fallback themes use generic generator names (Dark/Light/Random/...).
    // Send the display name to the parser when curated, seedColor otherwise.
    // This avoids any dependency on THEME_GROUPS being populated at render time.
    const isCurated = !SEED_DERIVED_NAMES.has(theme.name)
    const presetName = isCurated ? theme.name : undefined
    const seedColor = isCurated ? undefined : (theme.seedColor ?? undefined)
    const apiView = groupBy === 'actor' ? 'actors' : groupBy === 'location' ? 'locations' : undefined
    const { result, isProcessing, error } = useENParser(source, theme.isDark ?? themeName === 'dark', apiView, seedColor, presetName, direction)

    // Sync parser results to store
    useEffect(() => {
        usePlayground.setState({ isProcessing })
    }, [isProcessing])

    useEffect(() => {
        usePlayground.getState().setAnalysis(result ?? null, error ?? null)
    }, [result, error])

    // ── Keyboard shortcuts (Ctrl+A, Escape) ──
    useEffect(() => {
        const cleanupSelectAll = onKeyCombo('a', { ctrl: true }, () => {
            usePlayground.getState().selectAll()
        })
        const cleanupEscape = onKeyCombo('Escape', {}, () => {
            usePlayground.getState().clearSelection()
        })
        return () => { cleanupSelectAll(); cleanupEscape() }
    }, [])

    // ── Beforeunload — warn if source modified ──
    const initialSourceRef = useRef(source)
    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (usePlayground.getState().source !== initialSourceRef.current) {
                e.preventDefault()
            }
        }
        window.addEventListener('beforeunload', handler)
        return () => window.removeEventListener('beforeunload', handler)
    }, [])

    // ── URL sync — debounced replaceState with ?code= ──
    useEffect(() => {
        const timeout = setTimeout(() => {
            const url = new URL(window.location.href)
            url.searchParams.set('code', source)
            history.replaceState(null, '', url.toString())
        }, 2000)
        return () => clearTimeout(timeout)
    }, [source])

    // Embed-mode renders a single pane full-bleed. GraphPanel for the
    // canvas, StructurePanel for source+findings (it contains the
    // source editor), Sidebar for the narrative/tab panel. No header,
    // no resize handles, no privacy notice — the parent page owns
    // chrome.
    const viewMode = readViewMode()
    if (viewMode === 'canvas') {
        return <div className="w-full h-full bg-bg text-text"><GraphPanel /></div>
    }
    if (viewMode === 'editor') {
        return <div className="w-full h-full bg-bg text-text"><StructurePanel /></div>
    }
    if (viewMode === 'structure') {
        return <div className="w-full h-full bg-bg text-text"><Sidebar /></div>
    }
    // Split modes stack vertically on narrow viewports. The landing
    // page embeds split iframes at a fixed height, so on desktop
    // (md≥) the two panes live side-by-side; on mobile they stack so
    // neither pane collapses to an unreadable sliver.
    if (viewMode === 'split-canvas') {
        return (
            <div className="w-full h-full bg-bg text-text flex flex-col md:flex-row">
                <div className="flex-1 min-w-0 min-h-0 overflow-hidden" style={{ background: 'var(--bg-subtle)' }}>
                    <StructurePanel />
                </div>
                <div className="flex-1 min-w-0 min-h-0 overflow-hidden">
                    <GraphPanel />
                </div>
            </div>
        )
    }
    if (viewMode === 'split-structure') {
        return (
            <div className="w-full h-full bg-bg text-text flex flex-col md:flex-row">
                <div className="flex-1 min-w-0 min-h-0 overflow-hidden" style={{ background: 'var(--bg-subtle)' }}>
                    <StructurePanel />
                </div>
                <div className="flex-1 min-w-0 min-h-0 overflow-hidden" style={{ background: 'var(--bg-subtle)' }}>
                    <Sidebar />
                </div>
            </div>
        )
    }
    return <AppShell />
}

export default function Playground() {
    return (
        <div className="h-[100dvh] overflow-hidden">
            <PlaygroundContent />
        </div>
    )
}
